import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const DIETARY_LABELS = ['Vegan', 'Vegetarian', 'Gluten-Free', 'Nut-Free', 'Dairy-Free', 'Halal', 'Kosher'];
const DIETARY_SHORT = { 'Vegan': 'V', 'Vegetarian': 'VG', 'Gluten-Free': 'GF', 'Nut-Free': 'NF', 'Dairy-Free': 'DF', 'Halal': 'H', 'Kosher': 'K' };

function PublicMenu() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeMenu, setActiveMenu] = useState(0);
  const [activeCategory, setActiveCategory] = useState(0);
  const [filter, setFilter] = useState([]);
  const [search, setSearch] = useState('');
  const [hoursOpen, setHoursOpen] = useState(false);
  const categoryRefs = useRef([]);
  const menuParam = new URLSearchParams(window.location.search).get('menu');

  useEffect(() => {
    axios.get((process.env.REACT_APP_API_URL || '') + '/api/public/menu/' + slug)
      .then(res => {
        setData(res.data);
        if (menuParam && res.data.menus) {
          const idx = res.data.menus.findIndex(m => m.id === parseInt(menuParam));
          if (idx >= 0) setActiveMenu(idx);
        }
      })
      .catch(() => setError('This menu is currently unavailable.'))
      .finally(() => setLoading(false));
  }, [slug, menuParam]);

  const toggleFilter = (label) => {
    setFilter(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const display = hour % 12 || 12;
    return display + ':' + m + ' ' + ampm;
  };

  const pluraliseCategoryName = (name) => {
    const lower = name.toLowerCase();
    if (lower === 'beverage') return 'Beverages';
    if (lower === 'main' || lower === 'mains') return 'Mains';
    if (lower === 'starter' || lower === 'starters') return 'Starters';
    if (lower === 'dessert' || lower === 'desserts') return 'Desserts';
    if (lower === 'side' || lower === 'sides') return 'Sides';
    if (lower === 'snack' || lower === 'snacks') return 'Snacks';
    if (lower === 'drink' || lower === 'drinks') return 'Drinks';
    return name;
  };

  const isOpenNow = (todayHours) => {
    if (!todayHours || todayHours.is_closed) return false;
    if (!todayHours.open_time || !todayHours.close_time) return false;
    const now = new Date();
    const [oh, om] = todayHours.open_time.split(':').map(Number);
    const [ch, cm] = todayHours.close_time.split(':').map(Number);
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const openMins = oh * 60 + om;
    const closeMins = ch * 60 + cm;
    if (closeMins < openMins) return nowMins >= openMins || nowMins < closeMins;
    return nowMins >= openMins && nowMins < closeMins;
  };

  const scrollToCategory = (index) => {
    setActiveCategory(index);
    if (categoryRefs.current[index]) {
      categoryRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const backendUrl = process.env.REACT_APP_API_URL || '';

  const imgUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : backendUrl + url;
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7f5f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '28px', height: '28px', border: '2px solid #ddd', borderTop: '2px solid #1a1a1a', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7f5f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', padding: '32px 24px' }}>
        <p style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a', marginBottom: '8px' }}>Menu unavailable</p>
        <p style={{ fontSize: '13px', color: '#888' }}>{error}</p>
      </div>
    </div>
  );

  const { restaurant, opening_hours, menus } = data;
  const isDark = restaurant.theme === 'dark';
  const currentMenu = menus[activeMenu];
  const todayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date().getDay()];
  const todayHours = opening_hours.find(h => h.day_of_week === todayName);
  const openNow = isOpenNow(todayHours);

  const allItems = currentMenu ? currentMenu.categories.flatMap(c => c.items) : [];
  const availableFilters = allItems.length > 5
    ? DIETARY_LABELS.filter(label => allItems.filter(item => item.dietary_labels && item.dietary_labels.includes(label)).length >= 2)
    : [];

  const filteredCategories = currentMenu ? currentMenu.categories.map(cat => {
    const matched = cat.items.filter(item => {
      const matchesFilter = filter.length === 0 || filter.every(f => item.dietary_labels && item.dietary_labels.includes(f));
      const matchesSearch = search === '' ||
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(search.toLowerCase()));
      return matchesFilter && matchesSearch;
    });
    const available = matched.filter(i => i.is_available);
    const unavailable = matched.filter(i => !i.is_available);
    return { ...cat, items: [...available, ...unavailable] };
  }).filter(cat => cat.items.length > 0) : [];

  const todayLabel = todayHours
    ? todayHours.is_closed ? 'Closed today'
    : 'Today: ' + formatTime(todayHours.open_time) + ' - ' + formatTime(todayHours.close_time)
    : '';

  if (isDark) {
    return (
      <div style={d.page}>
        <div style={d.hero}>
          <div style={d.heroOverlay} />
          {restaurant.cover_url && <img src={imgUrl(restaurant.cover_url)} alt="Cover" style={d.heroCoverImg} />}
          {restaurant.logo_url ? (
            <img src={imgUrl(restaurant.logo_url)} alt="Logo" style={d.heroLogo} />
          ) : (
            <div style={d.heroLogoPlaceholder}><span style={d.heroLogoInitial}>{restaurant.name.charAt(0)}</span></div>
          )}
          <h1 style={d.heroName}>{restaurant.name}</h1>
          {restaurant.description && <p style={d.heroDesc}>{restaurant.description}</p>}
          <span style={openNow ? d.openBadge : d.closedBadge}>{openNow ? 'Open now' : 'Closed'}</span>
        </div>

        <div style={d.topBar}>
          <div style={d.topBarMeta}>
            {restaurant.address && <span style={d.topBarItem}>📍 {restaurant.address}</span>}
            {restaurant.phone && <a href={'tel:' + restaurant.phone} style={d.topBarItem}>📞 {restaurant.phone}</a>}
          </div>
          {restaurant.instagram_handle && (
            <a href={'https://instagram.com/' + (restaurant.instagram_handle.startsWith('@') ? restaurant.instagram_handle.slice(1) : restaurant.instagram_handle)} target="_blank" rel="noreferrer" style={d.instaLink}>
              {restaurant.instagram_handle.startsWith('@') ? restaurant.instagram_handle : '@' + restaurant.instagram_handle}
            </a>
          )}
        </div>

        {opening_hours.length > 0 && (
          <div>
            <button style={d.hoursToggle} onClick={() => setHoursOpen(!hoursOpen)}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '600', color: openNow ? '#81c784' : '#e57373' }}>{todayLabel}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', textDecoration: 'underline' }}>{hoursOpen ? 'Hide hours' : 'View full hours'}</div>
              </div>
              <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.3)' }}>v</span>
            </button>
            {hoursOpen && (
              <div style={d.hoursDrawer}>
                {opening_hours.map(h => (
                  <div key={h.day_of_week} style={{ ...d.hoursRow, fontWeight: h.day_of_week === todayName ? '600' : '400' }}>
                    <span style={d.hoursDay}>{h.day_of_week}</span>
                    <span style={{ fontSize: '12px', color: h.is_closed ? '#e57373' : 'rgba(255,255,255,0.6)' }}>
                      {h.is_closed ? 'Closed' : formatTime(h.open_time) + ' - ' + formatTime(h.close_time)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {menus.length > 1 && (
          <div style={d.menuTabs}>
            {menus.map((menu, i) => (
              <button key={menu.id} style={{ ...d.menuTab, ...(activeMenu === i ? d.menuTabActive : {}) }}
                onClick={() => { setActiveMenu(i); setActiveCategory(0); }}>
                {menu.name}
              </button>
            ))}
          </div>
        )}

        <div style={d.catNav}>
          {filteredCategories.map((cat, i) => (
            <button key={cat.id} style={{ ...d.catNavBtn, ...(activeCategory === i ? d.catNavBtnActive : {}) }}
              onClick={() => scrollToCategory(i)}>
              {pluraliseCategoryName(cat.name)} ({cat.items.length})
            </button>
          ))}
        </div>

        <div style={d.searchWrap}>
          <span style={d.searchIcon}>🔍</span>
          <input style={d.searchInput} type="text" placeholder="Search dishes, drinks, or ingredients..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button style={d.clearSearch} onClick={() => setSearch('')}>x</button>}
        </div>

        {availableFilters.length > 0 && (
          <div style={d.filterWrap}>
            {availableFilters.map(label => (
              <button key={label}
                style={filter.includes(label) ? { ...d.filterBtn, ...d.filterBtnActive } : d.filterBtn}
                onClick={() => toggleFilter(label)}>
                {label}
              </button>
            ))}
          </div>
        )}

        <div style={d.menuBody}>
          {filteredCategories.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>No items match your search.</div>
          ) : (
            filteredCategories.map((cat, catIdx) => (
              <div key={cat.id} ref={el => categoryRefs.current[catIdx] = el}>
                <div style={d.catBanner}>
                  <div style={d.catBannerOverlay} />
                  <h2 style={d.catBannerTitle}>{pluraliseCategoryName(cat.name).toUpperCase()}</h2>
                </div>
                <div style={d.itemsBlock}>
                  {cat.items.map(item => (
                    <div key={item.id} style={{ ...d.itemRow, opacity: item.is_available ? 1 : 0.45 }}>
                      <div style={d.itemLeft}>
                        <div>
                          <div style={d.itemName}>{item.name.toUpperCase()}</div>
                          {item.description && <div style={d.itemDesc}>{item.description}</div>}
                        </div>
                        <div style={d.itemBottom}>
                          <span style={d.itemPrice}>&#8358;{Number(item.price).toLocaleString()}</span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {item.dietary_labels && item.dietary_labels.map(l => (
                              <span key={l} style={d.badge}>{DIETARY_SHORT[l] || l}</span>
                            ))}
                            {!item.is_available && <span style={d.soldOutBadge}>SOLD OUT</span>}
                          </div>
                        </div>
                      </div>
                      <div style={d.itemImgWrap}>
                        {item.image_url ? (
                          <img src={imgUrl(item.image_url)} alt={item.name} style={d.itemImg} />
                        ) : (
                          <div style={d.itemImgPlaceholder} />
                        )}
                        {!item.is_available && (
                          <div style={d.soldOutOverlay}><span style={d.soldOutLabel}>SOLD OUT</span></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div style={d.footer}>
          <div style={d.footerName}>{restaurant.name}</div>
          <div style={d.footerMeta}>
            {restaurant.address && <div>{restaurant.address}</div>}
            {todayHours && !todayHours.is_closed && <div>{formatTime(todayHours.open_time)} - {formatTime(todayHours.close_time)}</div>}
            {restaurant.phone && <div>{restaurant.phone}</div>}
          </div>
          <div style={d.footerIcons}>
            {restaurant.instagram_handle && (
              <a href={'https://instagram.com/' + (restaurant.instagram_handle.startsWith('@') ? restaurant.instagram_handle.slice(1) : restaurant.instagram_handle)} target="_blank" rel="noreferrer" style={d.footerIcon}>IG</a>
            )}
            {restaurant.phone && (
              <a href={'tel:' + restaurant.phone} style={d.footerIcon}>📞</a>
            )}
          </div>
          <div style={d.footerCopy}>Powered by Menu Board</div>
        </div>
        <style>{'@keyframes spin { to { transform: rotate(360deg); } } * { box-sizing: border-box; margin: 0; padding: 0; }'}</style>
      </div>
    );
  }

  return (
    <div style={l.page}>
      <div style={l.coverWrap}>
        {restaurant.cover_url && <img src={imgUrl(restaurant.cover_url)} alt="Cover" style={l.coverImg} />}
        {restaurant.logo_url ? (
          <img src={imgUrl(restaurant.logo_url)} alt="Logo" style={l.avatarOverlap} />
        ) : (
          <div style={l.avatarOverlapPlaceholder}>
            <span style={l.avatarInitial}>{restaurant.name.charAt(0).toUpperCase()}</span>
          </div>
        )}
      </div>

      <div style={l.profileHeader}>
        <div style={l.nameRow}>
          <h1 style={l.profileName}>{restaurant.name}</h1>
          <span style={openNow ? l.openBadge : l.closedBadge}>{openNow ? 'Open now' : 'Closed'}</span>
        </div>
        {restaurant.description && <p style={l.profileDesc}>{restaurant.description}</p>}
        <div style={l.profileMeta}>
          {restaurant.address && <div style={l.metaRow}><span>📍</span><span>{restaurant.address}</span></div>}
          {restaurant.phone && <a href={'tel:' + restaurant.phone} style={l.metaRowLink}><span>📞</span><span>{restaurant.phone}</span></a>}
          {restaurant.instagram_handle && (
            <a href={'https://instagram.com/' + (restaurant.instagram_handle.startsWith('@') ? restaurant.instagram_handle.slice(1) : restaurant.instagram_handle)} target="_blank" rel="noreferrer" style={l.metaInsta}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="1" fill="#c0392b" stroke="none"/>
              </svg>
              <span>{restaurant.instagram_handle.startsWith('@') ? restaurant.instagram_handle : '@' + restaurant.instagram_handle}</span>
            </a>
          )}
        </div>

        {opening_hours.length > 0 && (
          <button style={l.hoursToggle} onClick={() => setHoursOpen(!hoursOpen)}>
            <div style={l.hoursToggleLeft}>
              <span style={{ fontSize: '11px', color: openNow ? '#2e7d32' : '#c0392b', fontWeight: '600' }}>{todayLabel}</span>
              <span style={l.hoursToggleHint}>{hoursOpen ? 'Hide hours' : 'View full hours'}</span>
            </div>
            <span style={l.chevron}>v</span>
          </button>
        )}

        {hoursOpen && (
          <div style={l.hoursDrawer}>
            {opening_hours.map(h => (
              <div key={h.day_of_week} style={{ ...l.hoursRow, fontWeight: h.day_of_week === todayName ? '600' : '400' }}>
                <span style={l.hoursDay}>{h.day_of_week}</span>
                <span style={{ fontSize: '12px', color: h.is_closed ? '#c0392b' : '#444' }}>
                  {h.is_closed ? 'Closed' : formatTime(h.open_time) + ' - ' + formatTime(h.close_time)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={l.divider} />

      {menus.length > 1 && (
        <div style={l.menuTabs}>
          {menus.map((menu, i) => (
            <button key={menu.id} style={{ ...l.menuTab, ...(activeMenu === i ? l.menuTabActive : {}) }}
              onClick={() => { setActiveMenu(i); setActiveCategory(0); }}>
              {menu.name}
            </button>
          ))}
        </div>
      )}

      <div style={l.searchWrap}>
        <span style={l.searchIcon}>🔍</span>
        <input style={l.searchInput} type="text" placeholder="Search dishes, drinks, or ingredients..."
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {search && <button style={l.clearSearch} onClick={() => setSearch('')}>x</button>}
      </div>

      {availableFilters.length > 0 && (
        <div style={l.filterWrap}>
          {availableFilters.map(label => (
            <button key={label}
              style={filter.includes(label) ? { ...l.filterBtn, backgroundColor: '#1a1a1a', color: '#fff', borderColor: '#1a1a1a' } : l.filterBtn}
              onClick={() => toggleFilter(label)}>
              {label}
            </button>
          ))}
        </div>
      )}

      {filteredCategories.length > 1 && (
        <div style={l.catNav}>
          {filteredCategories.map((cat, i) => (
            <button key={cat.id} style={{ ...l.catNavBtn, ...(activeCategory === i ? l.catNavBtnActive : {}) }}
              onClick={() => scrollToCategory(i)}>
              {pluraliseCategoryName(cat.name)} <span style={{ fontSize: '10px', color: '#ccc' }}>({cat.items.length})</span>
            </button>
          ))}
        </div>
      )}

      <div style={l.menuBody}>
        {filteredCategories.length === 0 ? (
          <div style={l.noResults}><p style={{ fontSize: '13px', color: '#aaa' }}>No items match your search.</p></div>
        ) : (
          filteredCategories.map((cat, catIdx) => (
            <div key={cat.id} ref={el => categoryRefs.current[catIdx] = el} style={l.catSection}>
              <div style={l.catHeader}>
                <h2 style={l.catTitle}>{pluraliseCategoryName(cat.name)}</h2>
                <span style={l.catItemCount}>{cat.items.filter(i => i.is_available).length} available</span>
              </div>
              {cat.items.map(item => (
                <div key={item.id} style={{ ...l.itemRow, opacity: item.is_available ? 1 : 0.5 }}>
                  <div style={l.itemInfo}>
                    <div style={l.itemTop}>
                      <h3 style={l.itemName}>{item.name}</h3>
                      <span style={l.itemPrice}>&#8358;{Number(item.price).toLocaleString()}</span>
                    </div>
                    {item.description && <p style={l.itemDesc}>{item.description}</p>}
                    <div style={l.itemFooter}>
                      {item.dietary_labels && item.dietary_labels.map(lbl => (
                        <span key={lbl} style={l.badge}>{DIETARY_SHORT[lbl] || lbl}</span>
                      ))}
                      {!item.is_available && <span style={l.soldOut}>SOLD OUT</span>}
                      {item.allergen_info && <span style={l.allergen}>Allergens: {item.allergen_info}</span>}
                    </div>
                  </div>
                  {item.image_url && (
                    <div style={l.itemImgWrap}>
                      <img src={imgUrl(item.image_url)} alt={item.name} style={l.itemImg} />
                      {!item.is_available && (
                        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: '10px', fontWeight: '700', color: '#fff', letterSpacing: '1px' }}>SOLD OUT</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <div style={l.footer}>
        <div style={l.footerDivider} />
        <p style={l.footerBrand}>MENU BOARD</p>
        <a href="/privacy" style={l.footerLink}>Privacy Policy</a>
      </div>
      <style>{'@keyframes spin { to { transform: rotate(360deg); } } * { box-sizing: border-box; margin: 0; padding: 0; }'}</style>
    </div>
  );
}

const d = {
  page: { minHeight: '100vh', backgroundColor: '#111', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', maxWidth: '600px', margin: '0 auto', color: '#fff' },
  hero: { position: 'relative', height: '220px', backgroundColor: '#1a1a1a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '20px', overflow: 'hidden' },
  heroOverlay: { position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1 },
  heroCoverImg: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 },
  heroLogo: { position: 'relative', zIndex: 2, width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.2)', marginBottom: '10px' },
  heroLogoPlaceholder: { position: 'relative', zIndex: 2, width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#333', border: '2px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' },
  heroLogoInitial: { fontSize: '20px', fontWeight: '700', color: '#fff' },
  heroName: { position: 'relative', zIndex: 2, fontSize: '22px', fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '4px' },
  heroDesc: { position: 'relative', zIndex: 2, fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '10px' },
  openBadge: { position: 'relative', zIndex: 2, fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(46,125,50,0.3)', color: '#81c784', padding: '3px 10px', borderRadius: '20px', border: '0.5px solid rgba(129,199,132,0.4)' },
  closedBadge: { position: 'relative', zIndex: 2, fontSize: '10px', fontWeight: '600', backgroundColor: 'rgba(192,57,43,0.3)', color: '#e57373', padding: '3px 10px', borderRadius: '20px', border: '0.5px solid rgba(229,115,115,0.4)' },
  topBar: { backgroundColor: '#111', borderBottom: '0.5px solid rgba(255,255,255,0.08)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  topBarMeta: { display: 'flex', flexDirection: 'column', gap: '3px' },
  topBarItem: { fontSize: '11px', color: 'rgba(255,255,255,0.45)', textDecoration: 'none' },
  instaLink: { fontSize: '11px', color: '#c0392b', textDecoration: 'none', fontWeight: '500', flexShrink: 0 },
  hoursToggle: { width: '100%', background: 'transparent', border: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.08)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  hoursDrawer: { backgroundColor: '#1a1a1a', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px', borderBottom: '0.5px solid rgba(255,255,255,0.08)' },
  hoursRow: { display: 'flex', justifyContent: 'space-between' },
  hoursDay: { fontSize: '12px', color: 'rgba(255,255,255,0.5)' },
  menuTabs: { display: 'flex', overflowX: 'auto', borderBottom: '0.5px solid rgba(255,255,255,0.08)', backgroundColor: '#111' },
  menuTab: { padding: '12px 16px', border: 'none', borderBottom: '2px solid transparent', backgroundColor: 'transparent', fontSize: '12px', fontWeight: '500', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', whiteSpace: 'nowrap' },
  menuTabActive: { color: '#fff', borderBottomColor: '#c9a96e' },
  catNav: { display: 'flex', overflowX: 'auto', borderBottom: '0.5px solid rgba(255,255,255,0.08)', backgroundColor: '#111', position: 'sticky', top: 0, zIndex: 10 },
  catNavBtn: { padding: '11px 14px', border: 'none', borderBottom: '2px solid transparent', backgroundColor: 'transparent', fontSize: '11px', fontWeight: '500', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  catNavBtnActive: { color: '#fff', borderBottomColor: '#c9a96e' },
  searchWrap: { padding: '10px 14px', backgroundColor: '#111', borderBottom: '0.5px solid rgba(255,255,255,0.08)', position: 'relative', display: 'flex', alignItems: 'center' },
  searchIcon: { position: 'absolute', left: '26px', fontSize: '12px' },
  searchInput: { width: '100%', padding: '8px 12px 8px 28px', backgroundColor: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.6)', outline: 'none' },
  clearSearch: { position: 'absolute', right: '20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '11px', cursor: 'pointer' },
  filterWrap: { display: 'flex', gap: '6px', padding: '8px 14px', overflowX: 'auto', borderBottom: '0.5px solid rgba(255,255,255,0.08)', backgroundColor: '#111' },
  filterBtn: { padding: '5px 12px', borderRadius: '20px', border: '0.5px solid rgba(255,255,255,0.15)', backgroundColor: 'transparent', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.5)', flexShrink: 0 },
  filterBtnActive: { backgroundColor: '#fff', color: '#1a1a1a', borderColor: '#fff' },
  menuBody: { paddingBottom: '40px' },
  catBanner: { position: 'relative', height: '90px', backgroundColor: '#222', display: 'flex', alignItems: 'flex-end', padding: '12px 16px' },
  catBannerOverlay: { position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.1) 100%)' },
  catBannerTitle: { position: 'relative', zIndex: 2, fontSize: '20px', fontWeight: '800', letterSpacing: '-0.5px', color: '#fff' },
  itemsBlock: { backgroundColor: '#111', padding: '0 16px' },
  itemRow: { padding: '14px 0', borderBottom: '0.5px solid rgba(255,255,255,0.07)', display: 'flex', gap: '12px', alignItems: 'flex-start' },
  itemLeft: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '96px' },
  itemName: { fontSize: '12px', fontWeight: '700', color: '#fff', letterSpacing: '0.3px', marginBottom: '4px' },
  itemDesc: { fontSize: '11px', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, flex: 1 },
  itemBottom: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' },
  itemPrice: { fontSize: '14px', fontWeight: '600', color: '#c9a96e' },
  itemImgWrap: { position: 'relative', flexShrink: 0 },
  itemImg: { width: '96px', height: '96px', objectFit: 'cover', borderRadius: '8px', display: 'block' },
  itemImgPlaceholder: { width: '96px', height: '96px', borderRadius: '8px', backgroundColor: '#222' },
  soldOutOverlay: { position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  soldOutLabel: { fontSize: '9px', fontWeight: '700', color: 'rgba(255,255,255,0.7)', letterSpacing: '1px' },
  badge: { fontSize: '9px', backgroundColor: 'rgba(46,125,50,0.25)', color: '#81c784', padding: '2px 6px', borderRadius: '4px', fontWeight: '700', border: '0.5px solid rgba(129,199,132,0.3)' },
  soldOutBadge: { fontSize: '9px', color: 'rgba(255,255,255,0.35)', fontWeight: '700', letterSpacing: '0.5px' },
  footer: { backgroundColor: '#0d0d0d', padding: '28px 16px', textAlign: 'center', borderTop: '0.5px solid rgba(255,255,255,0.08)' },
  footerName: { fontSize: '16px', fontWeight: '800', color: 'rgba(255,255,255,0.9)', letterSpacing: '-0.3px', marginBottom: '12px' },
  footerMeta: { fontSize: '11px', color: 'rgba(255,255,255,0.35)', lineHeight: 1.9, marginBottom: '16px' },
  footerIcons: { display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '16px' },
  footerIcon: { width: '32px', height: '32px', borderRadius: '50%', border: '0.5px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' },
  footerCopy: { fontSize: '10px', color: 'rgba(255,255,255,0.2)' },
};

const l = {
  page: { minHeight: '100vh', backgroundColor: '#f7f5f0', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', maxWidth: '600px', margin: '0 auto' },
  coverWrap: { width: '100%', height: '180px', overflow: 'visible', position: 'relative', backgroundColor: '#c8c0b8' },
  coverImg: { width: '100%', height: '180px', objectFit: 'cover', display: 'block' },
  avatarOverlap: { position: 'absolute', bottom: '-28px', left: '16px', width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #f7f5f0', zIndex: 2 },
  avatarOverlapPlaceholder: { position: 'absolute', bottom: '-28px', left: '16px', width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#e0ddd8', border: '3px solid #f7f5f0', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 },
  avatarInitial: { fontSize: '22px', fontWeight: '700', color: '#999' },
  profileHeader: { padding: '36px 20px 14px', backgroundColor: '#f7f5f0' },
  nameRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '3px' },
  profileName: { fontSize: '20px', fontWeight: '800', letterSpacing: '-0.5px', color: '#1a1a1a' },
  openBadge: { fontSize: '10px', fontWeight: '600', backgroundColor: '#f0faf4', color: '#2e7d32', padding: '3px 8px', borderRadius: '20px', border: '0.5px solid #c8e6c9', whiteSpace: 'nowrap' },
  closedBadge: { fontSize: '10px', fontWeight: '600', backgroundColor: '#fdecea', color: '#c0392b', padding: '3px 8px', borderRadius: '20px', border: '0.5px solid #f5c6c6', whiteSpace: 'nowrap' },
  profileDesc: { fontSize: '12px', color: '#888', lineHeight: 1.5, marginBottom: '8px' },
  profileMeta: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' },
  metaRow: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#555' },
  metaRowLink: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#555', textDecoration: 'none' },
  metaInsta: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#c0392b', textDecoration: 'none', fontWeight: '500' },
  hoursToggle: { background: 'transparent', border: '0.5px solid #d8d5d0', borderRadius: '6px', padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' },
  hoursToggleLeft: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px' },
  hoursToggleHint: { fontSize: '11px', color: '#888', textDecoration: 'underline' },
  chevron: { fontSize: '8px', color: '#bbb' },
  hoursDrawer: { marginTop: '8px', padding: '14px', backgroundColor: '#fff', border: '0.5px solid #e0ddd8', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '8px' },
  hoursRow: { display: 'flex', justifyContent: 'space-between' },
  hoursDay: { fontSize: '12px', color: '#555' },
  divider: { height: '0.5px', backgroundColor: '#e0ddd8' },
  menuTabs: { display: 'flex', overflowX: 'auto', borderBottom: '0.5px solid #e0ddd8', backgroundColor: '#f7f5f0' },
  menuTab: { padding: '12px 18px', border: 'none', borderBottom: '2px solid transparent', backgroundColor: 'transparent', fontSize: '13px', fontWeight: '500', color: '#bbb', cursor: 'pointer', whiteSpace: 'nowrap' },
  menuTabActive: { color: '#1a1a1a', borderBottomColor: '#1a1a1a' },
  searchWrap: { padding: '12px 20px', borderBottom: '0.5px solid #e0ddd8', position: 'relative', display: 'flex', alignItems: 'center' },
  searchIcon: { position: 'absolute', left: '32px', fontSize: '14px' },
  searchInput: { width: '100%', padding: '9px 32px 9px 32px', border: '0.5px solid #e0ddd8', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff', outline: 'none', color: '#1a1a1a' },
  clearSearch: { position: 'absolute', right: '30px', background: 'none', border: 'none', color: '#bbb', fontSize: '11px', cursor: 'pointer' },
  filterWrap: { display: 'flex', gap: '6px', padding: '10px 20px', overflowX: 'auto', borderBottom: '0.5px solid #e0ddd8' },
  filterBtn: { padding: '6px 14px', borderRadius: '20px', border: '0.5px solid #ccc', backgroundColor: 'transparent', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap', color: '#666', flexShrink: 0, fontWeight: '500' },
  catNav: { display: 'flex', overflowX: 'auto', borderBottom: '0.5px solid #e0ddd8', backgroundColor: '#f7f5f0', position: 'sticky', top: 0, zIndex: 10 },
  catNavBtn: { padding: '11px 14px', border: 'none', borderBottom: '2px solid transparent', backgroundColor: 'transparent', fontSize: '12px', fontWeight: '500', color: '#888', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  catNavBtnActive: { color: '#1a1a1a', borderBottomColor: '#1a1a1a' },
  menuBody: { padding: '0 0 60px' },
  noResults: { padding: '60px 20px', textAlign: 'center' },
  catSection: { padding: '24px 20px 0' },
  catHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px' },
  catTitle: { fontSize: '18px', fontWeight: '700', color: '#1a1a1a', letterSpacing: '-0.3px' },
  catItemCount: { fontSize: '11px', color: '#bbb' },
  itemRow: { display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '14px 0', borderBottom: '0.5px solid #ede9e4' },
  itemInfo: { flex: 1 },
  itemTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '4px' },
  itemName: { fontSize: '14px', fontWeight: '600', color: '#1a1a1a', flex: 1, lineHeight: 1.3 },
  itemPrice: { fontSize: '14px', fontWeight: '700', color: '#1a1a1a', flexShrink: 0 },
  itemDesc: { fontSize: '12px', color: '#888', lineHeight: 1.5, marginBottom: '6px' },
  itemFooter: { display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' },
  itemImgWrap: { position: 'relative', flexShrink: 0 },
  itemImg: { width: '96px', height: '96px', objectFit: 'cover', borderRadius: '8px' },
  badge: { fontSize: '10px', backgroundColor: '#f0faf4', color: '#2e7d32', padding: '2px 7px', borderRadius: '4px', fontWeight: '600', border: '0.5px solid #c8e6c9' },
  soldOut: { fontSize: '10px', backgroundColor: '#fdecea', color: '#c0392b', padding: '2px 7px', borderRadius: '4px', fontWeight: '700', border: '0.5px solid #f5c6c6' },
  allergen: { fontSize: '10px', color: '#bbb' },
  footer: { padding: '28px 20px', textAlign: 'center' },
  footerDivider: { height: '0.5px', backgroundColor: '#e0ddd8', marginBottom: '16px' },
  footerBrand: { fontSize: '10px', letterSpacing: '3px', color: '#ccc', marginBottom: '6px' },
  footerLink: { fontSize: '10px', color: '#bbb', textDecoration: 'underline' },
};

export default PublicMenu;
